import type { SharedDocument } from "@casual-simulation/aux-common/documents/SharedDocument";
import { createRecordsClient } from "@casual-simulation/aux-records/RecordsClient";
import { SocketManager as WebsocketManager } from "@casual-simulation/websocket";
import { WebsocketConnectionClient } from "@casual-simulation/aux-websocket";
import stringify from "@casual-simulation/fast-json-stable-stringify";
import { isArrayBuffer } from "es-toolkit";
import { v4 as uuid } from "uuid";
import type { RecordFileFailure } from "@casual-simulation/aux-records";
import { InstRecordsClient } from "@casual-simulation/aux-common/websockets/InstRecordsClient";
import { PartitionAuthSource } from "@casual-simulation/aux-common/partitions/PartitionAuthSource";
import { AuthenticatedConnectionClient } from "@casual-simulation/aux-common/websockets/AuthenticatedConnectionClient";
import { computed, effect, signal } from "@preact/signals";
import {
  parseSessionKey,
  generateV1ConnectionToken,
} from "@casual-simulation/aux-common";
import { sha256 } from "hash.js";
import { first, firstValueFrom } from "rxjs";
import { guardRecordsClient } from "./SessionGuard";
import type { SessionInvalidatedEvent } from "./SessionGuard";

export type CasualOSManager = ReturnType<typeof CasualOSManager>;

export type {
  FatalSessionErrorCode,
  SessionInvalidatedEvent,
} from "./SessionGuard";
export { FATAL_SESSION_ERROR_CODES } from "./SessionGuard";

export interface UserInfo {
  id: string;
  email: string;
}

/**
 * The `beforeinstallprompt` event fired by Chromium browsers when the app is
 * eligible for installation. Not part of the standard TS DOM lib, so we declare
 * the shape we rely on here.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const UNSAFE_HEADERS = new Set([
  "accept-encoding",
  "referer",
  "sec-fetch-dest",
  "sec-fetch-mode",
  "sec-fetch-site",
  "origin",
  "sec-ch-ua-platform",
  "user-agent",
  "sec-ch-ua-mobile",
  "sec-ch-ua",
  "content-length",
  "connection",
  "host",
]);

export function CasualOSManager(
  endpoint: string = "https://auth.seedbible.org"
) {
  const rawClient = createRecordsClient(endpoint);
  const connectionId = uuid();
  let currentWakeLock: WakeLockSentinel | null = null;

  // Captured `beforeinstallprompt` event, used to trigger the native PWA
  // install dialog on Chromium browsers. Only available once the browser deems
  // the app installable (never on iOS Safari, or if already installed).
  let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
  if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (event) => {
      // Prevent the browser's default mini-infobar so we can trigger the
      // prompt from our own onboarding UI instead.
      event.preventDefault();
      deferredInstallPrompt = event as BeforeInstallPromptEvent;
    });
    window.addEventListener("appinstalled", () => {
      // The event is single-use and no longer valid once installed.
      deferredInstallPrompt = null;
    });
  }

  let instRecordsClient: InstRecordsClient | null = null;
  let authSource: PartitionAuthSource | null = null;

  const sessionKey = signal<string | null>(null);
  const connectionKey = signal<string | null>(null);

  const parsedSessionKey = computed(() => {
    const parsed = parseSessionKey(sessionKey.value);
    if (parsed) {
      return {
        userId: parsed[0],
        sessionId: parsed[1],
        sessionSecret: parsed[2],
        expireTimeMs: parsed[3],
      };
    } else {
      return null;
    }
  });

  /**
   * Fires when the records/auth API reports that our session key is dead — expired,
   * unrecognised, or the account is banned. `LoginManager` watches this and clears
   * the local session; `OsManager` makes no decision about the UI.
   */
  const sessionInvalidated = signal<SessionInvalidatedEvent | null>(null);
  let sessionInvalidatedCount = 0;

  const client = guardRecordsClient(rawClient, {
    getSessionKey: () => sessionKey.peek(),
    onSessionInvalidated: (errorCode) => {
      console.warn(`[OsManager] The session is no longer valid: ${errorCode}`);
      sessionInvalidated.value = {
        errorCode,
        id: ++sessionInvalidatedCount,
      };
    },
  });

  function getInstClient(): InstRecordsClient {
    if (!instRecordsClient) {
      const url = new URL("wss://auth.seedbible.org");
      const manager = new WebsocketManager(url);
      manager.init();
      const client = new WebsocketConnectionClient(manager.socket);
      const authSource = getAuthSource();
      const connection = new AuthenticatedConnectionClient(client, authSource);
      instRecordsClient = new InstRecordsClient(connection);

      connection.connect();
    }

    return instRecordsClient;
  }

  function getAuthSource(): PartitionAuthSource {
    if (!authSource) {
      const source = (authSource = new PartitionAuthSource());
      source.onAuthMessage.subscribe((message) => {
        const provideIndicator = (recordName: string | null, inst: string) => {
          const key = connectionKey.value;
          // Send response back asynchronously so that we can ensure the requester is listening for the response
          setTimeout(() => {
            let token: string | null = null;
            if (key) {
              token = generateV1ConnectionToken(
                key,
                connectionId,
                recordName,
                inst
              );
            }

            source.sendAuthResponse({
              type: "response",
              success: true,
              origin: message.origin,
              indicator: token
                ? {
                    connectionToken: token,
                  }
                : {
                    connectionId: connectionId,
                  },
            });
          });
        };

        // TODO: handle other message types and error cases
        if (message.type === "request") {
          if (
            message.kind === "need_indicator" ||
            message.kind === "invalid_indicator"
          ) {
            provideIndicator(null, "seed-bible");
          } else if (message.kind === "not_authorized") {
            console.log("Handling not_authorized message:", message);
            if (message.reason?.type === "invalid_token") {
              const recordName = message.resource?.recordName;
              const inst = message.resource?.inst;
              const branch = message.resource?.branch;

              if (!recordName || !inst || !branch) {
                console.log(
                  `[AuthCoordinator] Invalid token request missing recordName, inst, or branch`
                );
                return;
              }

              // Only allow automatically loading branches that start with 'doc/'
              // This is a temporary solution to prevent loading actual existing inst data and instead only allow loading
              // shared documents from other records
              if (!branch.startsWith("doc/")) {
                console.error(
                  `[AuthCoordinator] Invalid token request branch does not start with 'doc/'`
                );
                return;
              }

              provideIndicator(recordName, inst);
            }
          }
        }
      });
    }

    return authSource;
  }

  async function getSharedDocument(
    recordName: string | null,
    inst: string,
    docName: string,
    options?: { markers?: string[] }
  ): Promise<SharedDocument> {
    const client = getInstClient();
    const authSource = getAuthSource();
    // Shared documents are the only thing in the app that needs Yjs (~77 KB
    // with lib0), and they only come into play for multiplayer sessions — so
    // the module is fetched here rather than at boot. This function is already
    // async and already waits on a network sync, so the extra await is free.
    const { RemoteYjsSharedDocument } =
      await import("@casual-simulation/aux-common/documents/RemoteYjsSharedDocument");
    const doc = new RemoteYjsSharedDocument(client, authSource, {
      recordName,
      inst,
      branch: `doc/${docName}`,
      markers: options?.markers ? options.markers : undefined,
    });

    doc.connect();

    await firstValueFrom(
      doc.onStatusUpdated.pipe(first((s) => s.type === "sync" && s.synced))
    );

    return doc;
  }

  /**
   * Forgets which peers the inst client thinks are connected to a shared
   * document's branch, so the next `repo/watch_branch_devices` is treated as
   * a fresh start.
   *
   * Workaround for a CasualOS client bug that permanently breaks presence
   * after a dropped connection: when the local socket goes down,
   * `InstRecordsClient` synthesizes a disconnect for every peer it knows
   * about but never removes them from its own `_connectedDevices` cache. On
   * reconnect it does re-request the device list, and the server does send
   * the full list back — but every replayed "connected" message is then
   * filtered out as a duplicate against that stale cache, so no peer (not
   * even ourselves) is ever reported as present again for the life of the
   * session. Clearing the cache first lets the replayed list through.
   *
   * Reaches into a private field, so it's written to quietly do nothing if a
   * future SDK version changes shape rather than throwing.
   */
  function clearBranchDeviceCache(
    recordName: string | null,
    inst: string,
    docName: string
  ): void {
    const internals = getInstClient() as unknown as {
      _connectedDevices?: Map<string, unknown>;
    };
    // Mirrors the SDK's own branch cache key: `${recordName ?? ""}/${inst}/${branch}`.
    const key = `${recordName ?? ""}/${inst}/doc/${docName}`;
    internals._connectedDevices?.delete?.(key);
  }

  effect(() => {
    client.sessionKey = sessionKey.value as string;
  });

  const listDataByMarker = async (
    // Despite the field's name, the records server resolves this the same
    // way it does a write's `recordKey` - either a bare record name or an
    // actual record key both work.
    recordName: string,
    marker: string,
    lastAddress?: string
  ) => {
    const result = await client.listData({
      recordName,
      marker,
      address: lastAddress,
    });

    return result;
  };

  return {
    client,
    connectionId,
    sessionKey,
    parsedSessionKey,
    connectionKey,
    clearBranchDeviceCache,
    sessionInvalidated,

    getData: async (recordName: string, address: string) => {
      const result = await client.getData({
        recordName,
        address,
      });

      return result;
    },

    recordData: async (
      recordKey: string,
      address: string,
      data: unknown,
      options: { marker?: string }
    ) => {
      console.log(
        `Recording data for record ${recordKey} at address ${address} with marker ${options.marker}:`,
        data
      );
      return await client.recordData({
        recordKey,
        address,
        data,
        markers: options.marker ? [options.marker] : undefined,
      });
    },

    eraseData: async (recordKey: string, address: string) => {
      return client.eraseData({
        recordKey,
        address,
      });
    },

    listDataByMarker,

    listAllDataByMarker: async (
      recordName: string,
      marker: string
    ): Promise<{
      success: boolean;
      items: { address: string; data: unknown }[];
    }> => {
      const allItems: { address: string; data: unknown }[] = [];
      let lastAddress: string | undefined;

      while (true) {
        const page = await listDataByMarker(recordName, marker, lastAddress);

        if (!page.success) {
          console.error("Error listing data:", page);
          throw new Error(`Error listing data: ${page.errorCode}`);
        }

        if (page.items.length === 0) {
          break;
        }

        for (const item of page.items) {
          allItems.push({ address: item.address, data: item.data });
        }

        lastAddress = page.items[page.items.length - 1]?.address;
      }

      return { success: true, items: allItems };
    },

    recordFile: async (
      recordKey: string,
      data: object | string | number | boolean,
      options: { mimeType?: string; marker?: string }
    ) => {
      const result = await uploadFile(
        recordKey,
        data,
        client,
        options.marker ? [options.marker] : undefined,
        options.mimeType
      );
      return {
        success: true,
        url: result.fileUrl,
      };
    },

    requestWakeLock: async () => {
      if ("wakeLock" in navigator) {
        try {
          currentWakeLock = await navigator.wakeLock.request("screen");
          currentWakeLock.addEventListener("release", () => {
            console.log("Wake Lock was released");
            currentWakeLock = null;
          });
          console.log("Wake Lock is active");
          return currentWakeLock;
        } catch (err) {
          console.error(`Unable to acquire Wake Lock:`, err);
        }
      }
      return null;
    },

    disableWakeLock: async () => {
      if (currentWakeLock) {
        await currentWakeLock.release();
        currentWakeLock = null;
        console.log("Wake Lock released");
      }
    },

    getSharedDocument,

    promptToInstallPWA: async (): Promise<{
      outcome: "accepted" | "dismissed";
      platform: string;
    }> => {
      if (!deferredInstallPrompt) {
        // No captured event: the browser doesn't support programmatic install
        // (e.g. iOS Safari), the app is already installed, or the prompt has
        // already been consumed.
        throw new Error("PWA installation is not available on this device");
      }

      const promptEvent = deferredInstallPrompt;
      // The event can only be used once — clear it before awaiting the choice.
      deferredInstallPrompt = null;

      await promptEvent.prompt();
      return promptEvent.userChoice;
    },
  };
}

/**
 * Uploads a file to the records server. Returns the URL of the file that was uploaded.
 * @param recordNameOrKey The name or key of the record to upload to.
 * @param data The data to upload
 * @param sessionKey The session key to use for authentication.
 */
export async function uploadFile(
  recordNameOrKey: string,
  data: object | string | number | boolean,
  client: ReturnType<typeof createRecordsClient>,
  markers: string[] = ["publicRead"],
  providedMimeType?: string
) {
  // Pinned to `ArrayBuffer` (rather than the default `ArrayBufferLike`) so it
  // satisfies `fetch`'s `BodyInit` when uploaded below. All three branches
  // already produce a plain-ArrayBuffer-backed view.
  let encodedData: Uint8Array<ArrayBuffer>;
  let mimeType: string;
  if (isArrayBuffer(data)) {
    encodedData = new Uint8Array(data);
    mimeType = providedMimeType || "application/octet-stream";
  } else if (data instanceof Blob) {
    encodedData = await data.bytes();
    mimeType = providedMimeType || data.type || "application/octet-stream";
  } else {
    const json = stringify(data);
    encodedData = new TextEncoder().encode(json);
    mimeType = providedMimeType || "application/json";
  }
  const byteLength = encodedData.byteLength;
  const hash = getHash(encodedData);

  const recordFileResult = await client.recordFile({
    recordKey: recordNameOrKey,
    fileSha256Hex: hash,
    fileMimeType: mimeType,
    fileByteLength: byteLength,
    markers: markers as [string, ...string[]],
  });

  let fileUrl: string;
  if (recordFileResult.success === false) {
    if (recordFileResult.errorCode !== "file_already_exists") {
      throw new Error(
        "Failed to record file: " +
          recordFileResult.errorCode +
          " " +
          recordFileResult.errorMessage
      );
    } else {
      fileUrl = (recordFileResult as RecordFileFailure).existingFileUrl!;
    }
  } else {
    const method = recordFileResult.uploadMethod;
    const url = (fileUrl = recordFileResult.uploadUrl);
    const headers = { ...recordFileResult.uploadHeaders };

    for (const header of UNSAFE_HEADERS) {
      delete headers[header];
    }

    const uploadResult = await fetch(url, {
      method,
      headers,
      body: encodedData,
    });

    if (!uploadResult.ok) {
      throw new Error(
        `Failed to upload file. (${uploadResult.status} ${uploadResult.statusText})`
      );
    } else {
      console.log("Successfully uploaded AUX file.");
    }
  }

  return {
    fileUrl,
    sha256Hash: hash,
  };
}

function getHash(buffer: Uint8Array): string {
  return sha256().update(buffer).digest("hex");
}
